import { useMemo, useState } from "react";
import {
  PlayCircle,
  Video,
  Sparkles,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Globe,
} from "lucide-react";
import { videos } from "../data/videos";

type VideoItem = {
  title: string;
  url: string;
  category?: string;
};

type PlaylistItem = {
  title: string;
  description: string;
  videoId: string;
  playlistUrl: string;
};

const featuredPlaylists: PlaylistItem[] = [
  {
    title: "Standalone Platform Training",
    description: "Full walkthrough of standalone platform setup and workflow.",
    videoId: "nK3ryQ7AeZQ",
    playlistUrl:
      "https://www.youtube.com/watch?v=nK3ryQ7AeZQ&list=PLUMc4nOdam99xT3w0ghOVCwHwfDupxr1X",
  },
  {
    title: "Frontend Training",
    description: "Customer-facing builder setup, layout, and frontend workflow.",
    videoId: "SLkgYVtBOMI",
    playlistUrl:
      "https://www.youtube.com/watch?v=SLkgYVtBOMI&list=PLUMc4nOdam99p8rBERYU6lwxY0rnagW6x",
  },
  {
    title: "Builder Features",
    description: "Learn the key tools and features inside the gang sheet builder.",
    videoId: "SLkgYVtBOMI",
    playlistUrl:
      "https://www.youtube.com/watch?v=SLkgYVtBOMI&list=PLUMc4nOdam9_a2r8kc5fEbgeat_HQFV-_",
  },
  {
    title: "WordPress Training",
    description: "Setup help, workflow guidance, and WordPress-specific tutorials.",
    videoId: "Y9znlvDRXTo",
    playlistUrl:
      "https://www.youtube.com/watch?v=Y9znlvDRXTo&list=PLUMc4nOdam99vK77kfmwXmYeWbfgor5b7",
  },
  {
    title: "Shopify Training",
    description: "Complete Shopify setup, integration, and workflow walkthroughs.",
    videoId: "JDRhVx56OFE",
    playlistUrl:
      "https://www.youtube.com/watch?v=JDRhVx56OFE&list=PLUMc4nOdam9-qFsfhZxTkeveXOC-q5ZRk",
  },
];

const DRIP_APPS_YOUTUBE_CHANNEL =
  "https://youtube.com/@dripapps?si=lWE-H-5-YUQCWX7g";

const INITIAL_VISIBLE_COUNT = 4;

function getYouTubeId(url: string) {
  const shortId = url.split("youtu.be/")[1]?.split("?")[0];
  if (shortId) return shortId;

  const longId = url.split("v=")[1]?.split("&")[0];
  if (longId) return longId;

  return null;
}

function getThumbnail(url: string) {
  const videoId = getYouTubeId(url);
  if (!videoId) return "";
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

function getPlaylistThumbnail(videoId: string) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function categoryOrderValue(category: string) {
  const order: Record<string, number> = {
    Install: 1,
    Beginner: 2,
    Software: 3,
    Troubleshooting: 4,
    Maintenance: 5,
    Hardware: 6,
    Pressing: 7,
    Powder: 8,
    Artwork: 9,
    Environment: 10,
    Other: 99,
  };

  return order[category] ?? 99;
}

function categoryDescription(category: string) {
  switch (category) {
    case "Install":
      return "Printer setup, machine install, and getting hardware online.";
    case "Beginner":
      return "Strong starting-point videos for new DTF users.";
    case "Software":
      return "RIP software, settings, margins, and print workflow help.";
    case "Troubleshooting":
      return "Fix common print issues faster with focused walkthroughs.";
    case "Maintenance":
      return "Cleaning, flushing, printhead care, and upkeep routines.";
    case "Hardware":
      return "Mechanical adjustments, rollers, tension, and machine setup.";
    case "Pressing":
      return "Heat press technique, application, and transfer finishing.";
    case "Powder":
      return "Powder application, curing, drying, and process control.";
    case "Artwork":
      return "Photoshop prep, white layer work, and file setup.";
    case "Environment":
      return "Climate, humidity, and room setup guidance.";
    default:
      return "Helpful DTF videos organized for faster learning.";
  }
}

function PlaylistCard({ playlist }: { playlist: PlaylistItem }) {
  const thumbnail = getPlaylistThumbnail(playlist.videoId);

  return (
    <div className="group relative overflow-hidden rounded-[24px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-4 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_24px_rgba(37,99,235,0.16)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-blue">
              <PlayCircle className="h-3.5 w-3.5" />
              Playlist
            </div>

            <h3 className="mt-3 text-xl font-bold tracking-tight text-white transition group-hover:text-dtf-blue">
              {playlist.title}
            </h3>
          </div>

          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue transition group-hover:border-dtf-blue/30 group-hover:bg-dtf-blue/10">
            <PlayCircle className="h-6 w-6" />
          </div>
        </div>

        <a
          href={playlist.playlistUrl}
          target="_blank"
          rel="noreferrer"
          className="group/block block"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/30">
            <div className="aspect-video w-full">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={playlist.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition duration-300 group-hover/block:scale-[1.02]"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-black/40 text-dtf-text-muted">
                  Preview unavailable
                </div>
              )}
            </div>

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600/90 shadow-[0_0_30px_rgba(239,68,68,0.35)]">
                <PlayCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </a>

        <p className="mt-4 text-sm leading-7 text-dtf-text-muted">
          {playlist.description}
        </p>

        <a
          href={playlist.playlistUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-sm text-dtf-text-muted transition hover:text-white"
        >
          Watch Playlist
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

function VideoCard({
  title,
  url,
}: {
  title: string;
  url: string;
}) {
  const thumbnail = getThumbnail(url);

  return (
    <div className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
              <Video className="h-3.5 w-3.5" />
              Video
            </div>

            <h3 className="mt-3 text-xl font-bold tracking-tight text-white transition group-hover:text-dtf-blue">
              {title}
            </h3>
          </div>

          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue transition group-hover:border-dtf-blue/30 group-hover:bg-dtf-blue/10">
            <PlayCircle className="h-6 w-6" />
          </div>
        </div>

        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="group/block block"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/30">
            <div className="aspect-video w-full">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition duration-300 group-hover/block:scale-[1.02]"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-black/40 text-dtf-text-muted">
                  Preview unavailable
                </div>
              )}
            </div>

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600/90 shadow-[0_0_30px_rgba(239,68,68,0.35)]">
                <PlayCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </a>

        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-sm text-dtf-text-muted transition hover:text-white"
        >
          Watch on YouTube
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

function CategoryAccordion({
  category,
  count,
  description,
  isOpen,
  onToggle,
  children,
}: {
  category: string;
  count: number;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  return (
    <BluePanel className="p-5 sm:p-6">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
            <Video className="h-3.5 w-3.5" />
            {category}
          </div>

          <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
            {category} Videos
          </h2>

          <p className="mt-2 max-w-3xl text-sm leading-7 text-dtf-text-muted">
            {description}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
            <PlayCircle className="h-4 w-4 text-dtf-blue" />
            <div className="text-sm text-dtf-text-muted">Videos</div>
            <div className="text-lg font-bold text-white">{count}</div>
          </div>

          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-white">
            {isOpen ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
      </button>

      {isOpen ? <div className="mt-6">{children}</div> : null}
    </BluePanel>
  );
}

function FeaturedTrainingAccordion({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <BluePanel className="mb-8 border-dtf-blue/25 p-6 sm:p-7">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div className="max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <PlayCircle className="h-3.5 w-3.5" />
            Featured Training
          </div>

          <h2 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            Gang Sheet Builder Training
          </h2>

          <p className="text-lg leading-8 text-dtf-text">
            Step-by-step playlist training for standalone setup, frontend workflow,
            builder features, WordPress integration, and Shopify integration.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
            <PlayCircle className="h-4 w-4 text-dtf-blue" />
            <div className="text-sm text-dtf-text-muted">Playlists</div>
            <div className="text-lg font-bold text-white">
              {featuredPlaylists.length}
            </div>
          </div>

          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-white">
            {isOpen ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
      </button>

      {isOpen ? (
        <>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://app.buildagangsheet.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-3 text-sm font-semibold text-white transition hover:border-dtf-blue/40 hover:bg-dtf-blue/15"
            >
              <Globe className="h-4 w-4 text-dtf-blue" />
              Visit Builder Website
              <ExternalLink className="h-4 w-4 text-dtf-accent" />
            </a>

            <a
              href={DRIP_APPS_YOUTUBE_CHANNEL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-white transition hover:border-red-500/35 hover:bg-red-500/15"
            >
              <PlayCircle className="h-4 w-4" />
              Drip Apps YouTube
              <ExternalLink className="h-4 w-4 text-dtf-accent" />
            </a>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {featuredPlaylists.map((playlist) => (
              <PlaylistCard key={playlist.title} playlist={playlist} />
            ))}
          </div>
        </>
      ) : null}
    </BluePanel>
  );
}

export default function Videos() {
  const normalizedVideos: VideoItem[] = useMemo(
    () =>
      videos.map((video) => ({
        ...video,
        category: video.category?.trim() || "Other",
      })),
    []
  );

  const groupedVideos = useMemo(
    () =>
      normalizedVideos.reduce<Record<string, VideoItem[]>>((acc, video) => {
        const category = video.category || "Other";

        if (!acc[category]) {
          acc[category] = [];
        }

        acc[category].push(video);
        return acc;
      }, {}),
    [normalizedVideos]
  );

  const sortedCategories = useMemo(
    () =>
      Object.keys(groupedVideos).sort(
        (a, b) => categoryOrderValue(a) - categoryOrderValue(b)
      ),
    [groupedVideos]
  );

  const [openCategory, setOpenCategory] = useState<string>("");
  const [featuredOpen, setFeaturedOpen] = useState(false);
  const [visibleCountByCategory, setVisibleCountByCategory] = useState<
    Record<string, number>
  >({});

  useMemo(() => {
    const initialCounts: Record<string, number> = {};
    sortedCategories.forEach((category) => {
      initialCounts[category] = INITIAL_VISIBLE_COUNT;
    });
    setVisibleCountByCategory(initialCounts);
  }, [sortedCategories]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <BluePanel className="mb-8">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <PlayCircle className="h-3.5 w-3.5" />
                Video Library
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Learn Visually
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Watch curated tutorials and walkthroughs to improve your DTF workflow,
                fix problems faster, and master your setup.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-black/20 px-5 py-4">
                <div className="mb-1 text-sm text-dtf-text-muted">Total Videos</div>
                <div className="text-2xl font-bold text-white">{videos.length}</div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 px-5 py-4">
                <div className="mb-1 text-sm text-dtf-text-muted">Categories</div>
                <div className="text-2xl font-bold text-white">
                  {sortedCategories.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      </BluePanel>

      <div className="mb-8 rounded-3xl border border-amber-400/20 bg-amber-500/10 px-5 py-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
          <p className="text-sm leading-7 text-amber-100/90">
            This page now loads one video category at a time to keep mobile scrolling
            smoother and reduce white-screen crashes.
          </p>
        </div>
      </div>

      <FeaturedTrainingAccordion
        isOpen={featuredOpen}
        onToggle={() => setFeaturedOpen((current) => !current)}
      />

      <div className="space-y-6">
        {sortedCategories.map((category) => {
          const sectionVideos = groupedVideos[category];
          const visibleCount =
            visibleCountByCategory[category] ?? INITIAL_VISIBLE_COUNT;
          const visibleVideos = sectionVideos.slice(0, visibleCount);
          const hasMore = visibleCount < sectionVideos.length;
          const isOpen = openCategory === category;

          return (
            <section key={category}>
              <CategoryAccordion
                category={category}
                count={sectionVideos.length}
                description={categoryDescription(category)}
                isOpen={isOpen}
                onToggle={() =>
                  setOpenCategory((current) =>
                    current === category ? "" : category
                  )
                }
              >
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {visibleVideos.map((video, index) => (
                    <VideoCard
                      key={`${category}-${video.title}-${index}`}
                      title={video.title}
                      url={video.url}
                    />
                  ))}
                </div>

                {hasMore ? (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleCountByCategory((current) => ({
                          ...current,
                          [category]: (current[category] ?? INITIAL_VISIBLE_COUNT) +
                            INITIAL_VISIBLE_COUNT,
                        }))
                      }
                      className="inline-flex items-center gap-2 rounded-2xl border border-dtf-blue/30 bg-dtf-blue/10 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/50 hover:bg-dtf-blue/15"
                    >
                      Show More Videos
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </CategoryAccordion>
            </section>
          );
        })}
      </div>

      <div className="mt-8 rounded-3xl border border-white/10 bg-black/20 px-5 py-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-dtf-blue" />
          <p className="text-sm leading-7 text-dtf-text-muted">
            Watching real-world examples helps you understand problems faster than
            reading alone. Use these videos alongside troubleshooting, how-to guides,
            and your issue library to speed up your learning curve.
          </p>
        </div>
      </div>
    </div>
  );
}